// Le plan d'épargne en actions : on y verse de l'argent, on y achète et on y vend des parts sans impôt,
// et l'impôt ne tombe que sur les gains qu'on en retire (règles simplifiées de 2026).
// - Plafond de 150 000 € de versements.
// - Frais de courtage : 0,5 % par ordre (le plafond légal dans un PEA), 1 € au moins.
// - Un retrait avant cinq ans ferme le PEA : tout est vendu, et 31,4 % des gains partent en impôts.
// - Après cinq ans, on peut retirer une partie : seuls les prélèvements sociaux (18,6 %) touchent les gains.
import { getAsset } from '../content/market';
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { AssetId, GameState } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';
import { addJournal, addNotice } from './actions';
import { type MarketMonth, priceOf, scheduleFirstDrop } from './market';

export const PEA = {
  ceiling: 150000,
  feeRate: 0.005,
  minFee: 1,
  years: 5,
  taxBefore: 0.314,
  taxAfter: 0.186,
  /** Les résultats gardés pour la moyenne : un an. */
  resultsKept: 12,
};

/** Les frais d'un ordre d'achat ou de vente. */
export function orderFee(amount: number): number {
  return round2(Math.max(PEA.minFee, amount * PEA.feeRate));
}

export function peaHoldingsValue(state: GameState): number {
  return Object.entries(state.pea.holdings).reduce(
    (sum, [id, holding]) => sum + (holding?.shares ?? 0) * priceOf(state, id as AssetId),
    0,
  );
}

/** Ce que vaut le PEA : l'argent qui attend, et les parts au prix du jour. */
export function peaValue(state: GameState): number {
  return state.pea.cash + peaHoldingsValue(state);
}

/** L'âge du PEA, en années : à cinq ans, ses gains ne paient plus que les prélèvements sociaux. */
export function peaAgeYears(state: GameState): number {
  return state.pea.open ? (state.day - state.pea.openDay) / DAYS_PER_YEAR : 0;
}

/** On peut ouvrir un PEA une fois le fonds d'urgence complet. */
export function canOpenPea(state: GameState): boolean {
  return !state.pea.open && state.stage === 'stage2';
}

export function openPea(state: GameState): void {
  if (!canOpenPea(state)) return;
  state.pea = { ...state.pea, open: true, openDay: state.day, cash: 0, deposits: 0, holdings: {}, flows: 0 };
  state.pea.valueAtMonthStart = 0;
  addJournal(state, 'journal.peaOpen');
}

/** Ce qu'on peut encore verser avant le plafond. */
export function peaRoom(state: GameState): number {
  return Math.max(0, round2(PEA.ceiling - state.pea.deposits));
}

/** Un versement du compte courant vers le PEA ; renvoie ce qui a été versé. */
export function depositToPea(state: GameState, amount: number): number {
  if (!state.pea.open) return 0;
  const value = round2(Math.min(amount, Math.max(0, state.cash), peaRoom(state)));
  if (value <= 0) return 0;
  state.cash = round2(state.cash - value);
  state.pea.cash = round2(state.pea.cash + value);
  state.pea.deposits = round2(state.pea.deposits + value);
  state.pea.flows = round2(state.pea.flows + value);
  state.year.saved += value;
  return value;
}

/** Le nombre de parts qu'on peut acheter avec l'argent qui attend dans le PEA, frais compris. */
export function maxShares(state: GameState, asset: AssetId): number {
  const price = priceOf(state, asset);
  let shares = Math.floor(state.pea.cash / (price * (1 + PEA.feeRate)));
  while (shares > 0 && shares * price + orderFee(shares * price) > state.pea.cash) shares -= 1;
  return shares;
}

/** Acheter des parts avec l'argent du PEA ; renvoie le nombre de parts achetées. */
export function buyShares(state: GameState, asset: AssetId, count: number): number {
  const shares = Math.min(Math.floor(count), maxShares(state, asset));
  if (!state.pea.open || shares <= 0) return 0;
  const amount = shares * priceOf(state, asset);
  const total = round2(amount + orderFee(amount));
  const holding = state.pea.holdings[asset] ?? { shares: 0, cost: 0 };
  state.pea.holdings = { ...state.pea.holdings, [asset]: { shares: holding.shares + shares, cost: round2(holding.cost + total) } };
  state.pea.cash = round2(state.pea.cash - total);
  scheduleFirstDrop(state);
  return shares;
}

/** Vendre des parts : l'argent reste dans le PEA, sans impôt tant qu'on ne le retire pas ; renvoie les parts vendues. */
export function sellShares(state: GameState, asset: AssetId, count: number): number {
  const holding = state.pea.holdings[asset];
  if (!holding) return 0;
  const shares = Math.min(Math.floor(count), holding.shares);
  if (shares <= 0) return 0;
  const amount = shares * priceOf(state, asset);
  const proceeds = round2(Math.max(0, amount - orderFee(amount)));
  const left = holding.shares - shares;
  const holdings = { ...state.pea.holdings };
  if (left > 0) holdings[asset] = { shares: left, cost: round2((holding.cost * left) / holding.shares) };
  else delete holdings[asset];
  state.pea.holdings = holdings;
  state.pea.cash = round2(state.pea.cash + proceeds);
  return shares;
}

/** L'impôt sur un retrait, et s'il ferme le PEA (avant cinq ans, on retire forcément tout). */
export function withdrawalPreview(state: GameState, amount: number): { amount: number; tax: number; closes: boolean } {
  const value = peaValue(state);
  const closes = peaAgeYears(state) < PEA.years;
  const withdrawn = closes ? value : Math.min(amount, state.pea.cash);
  const gainShare = value > 0 ? Math.max(0, (value - state.pea.deposits) / value) : 0;
  const rate = closes ? PEA.taxBefore : PEA.taxAfter;
  // Avant cinq ans, les frais de vente de tout le portefeuille s'ajoutent (estimés ici sans eux).
  return { amount: round2(withdrawn), tax: round2(withdrawn * gainShare * rate), closes };
}

/**
 * Retirer de l'argent du PEA vers le compte courant. Avant cinq ans, tout est vendu et le PEA se ferme ;
 * après, on retire l'argent qui attend dans le PEA, et seuls les gains qu'il contient sont imposés.
 */
export function withdrawFromPea(state: GameState, amount: number): void {
  if (!state.pea.open) return;
  if (peaAgeYears(state) < PEA.years) {
    closePea(state);
    return;
  }
  const value = peaValue(state);
  const withdrawn = round2(Math.min(amount, state.pea.cash));
  if (withdrawn <= 0 || value <= 0) return;
  const gainShare = Math.max(0, (value - state.pea.deposits) / value);
  const tax = round2(withdrawn * gainShare * PEA.taxAfter);
  // Le capital retiré sort des versements : le reste compte toujours pour le plafond et l'impôt.
  state.pea.deposits = round2(Math.max(0, state.pea.deposits - withdrawn * (1 - gainShare)));
  state.pea.cash = round2(state.pea.cash - withdrawn);
  state.pea.flows = round2(state.pea.flows - withdrawn);
  state.cash = round2(state.cash + withdrawn - tax);
  state.year.saved -= withdrawn;
  addJournal(state, 'journal.peaWithdraw', { amount: fmtEur(withdrawn), tax: fmtEur(tax) });
}

/** Fermer le PEA avant cinq ans : tout est vendu, et 31,4 % des gains partent en impôts. */
export function closePea(state: GameState): void {
  for (const asset of Object.keys(state.pea.holdings) as AssetId[]) {
    sellShares(state, asset, state.pea.holdings[asset]?.shares ?? 0);
  }
  const total = state.pea.cash;
  const gains = Math.max(0, total - state.pea.deposits);
  const tax = round2(gains * (peaAgeYears(state) < PEA.years ? PEA.taxBefore : PEA.taxAfter));
  state.cash = round2(state.cash + total - tax);
  state.year.saved -= total;
  state.pea = {
    ...state.pea,
    open: false,
    cash: 0,
    deposits: 0,
    holdings: {},
    monthly: 0,
    valueAtMonthStart: 0,
    flows: 0,
    lastMonthResult: 0,
    results: [],
    history: [],
    historyWeek: -1,
  };
  addJournal(state, 'journal.peaClosed', { amount: fmtEur(total), tax: fmtEur(tax) });
}

/** Chaque semaine de jeu, un point de la courbe : la valeur du PEA et ce qui a été versé. */
export function recordPeaHistory(state: GameState): void {
  const pea = state.pea;
  const week = Math.floor(state.day / 7);
  if (!pea.open || week <= pea.historyWeek) return;
  pea.historyWeek = week;
  pea.history = [...pea.history, { value: round2(peaValue(state)), deposits: round2(pea.deposits) }];
}

/** Ce que le versement programmé fait partir chaque mois : rien s'il est suspendu. */
export function peaMonthlyAmount(state: GameState): number {
  return state.pea.open && !state.pea.monthlyPaused ? state.pea.monthly : 0;
}

/** Suspendre le versement programmé, ou le reprendre : le montant réglé ne change pas. */
export function togglePeaMonthly(state: GameState): void {
  state.pea.monthlyPaused = !state.pea.monthlyPaused;
}

/** Le résultat moyen du PEA sur les douze derniers mois (gains et pertes, hors versements). */
export function peaAverageResult(state: GameState): number {
  const results = state.pea.results;
  return results.length > 0 ? results.reduce((sum, value) => sum + value, 0) / results.length : 0;
}

/**
 * Chaque mois : le résultat du mois écoulé (ce que le PEA a gagné ou perdu, hors versements et retraits),
 * puis le versement programmé, qui achète autant de parts entières que possible.
 */
export function peaMonth(state: GameState): void {
  const pea = state.pea;
  if (!pea.open) return;
  const value = peaValue(state);
  const result = round2(value - pea.valueAtMonthStart - pea.flows);
  pea.lastMonthResult = result;
  pea.results = [...pea.results, result].slice(-PEA.resultsKept);
  pea.flows = 0;
  pea.valueAtMonthStart = value;
  const monthly = peaMonthlyAmount(state);
  if (monthly > 0) {
    depositToPea(state, monthly);
    buyShares(state, pea.monthlyAsset, maxShares(state, pea.monthlyAsset));
  }
}

/** Les nouvelles du mois de bourse : les dividendes tombent dans le PEA, les grosses nouvelles s'affichent. */
export function applyMarketMonth(state: GameState, month: MarketMonth): void {
  if (!state.pea.open) return;
  for (const [id, perShare] of Object.entries(month.dividends) as [AssetId, number][]) {
    const shares = state.pea.holdings[id]?.shares ?? 0;
    if (shares <= 0) continue;
    const amount = round2(shares * perShare);
    state.pea.cash = round2(state.pea.cash + amount);
    addJournal(state, 'journal.dividend', { name: t(getAsset(id).name), amount: fmtEur(amount) });
  }
  for (const news of month.news) {
    const key = news.up ? 'market.newsUp' : 'market.newsDown';
    const params = { name: t(getAsset(news.asset).name) };
    addNotice(state, key, params);
    addJournal(state, key, params);
  }
}
